/**
 * Helpers for the What's New page.
 * Release notes live in public/whats-new.json so updates are visible
 * without requiring users to hard-refresh a cached JS bundle.
 *
 * Optional per-release `clinicTypes` (e.g. ["EYECONX"] or ["SMS_GATEWAY"])
 * filters which product surface sees that release. Omit to show for all.
 */
export const WHATS_NEW_STORAGE_KEY = 'eyeconx.whatsNew.dismissedVersion';

export function getLatestWhatsNewVersion(data, clinicType) {
    const releases = normalizeReleases(data, clinicType);
    return releases[0]?.version || null;
}

function releaseMatchesClinicType(release, clinicType) {
    if (!clinicType) {
        return true;
    }
    const types = release?.clinicTypes;
    if (!Array.isArray(types) || types.length === 0) {
        return true;
    }
    return types.includes(clinicType);
}

export function normalizeReleases(data, clinicType) {
    if (!data) {
        return [];
    }

    // New format: { releases: [ { version, date, title, features[] }, ... ] }
    if (Array.isArray(data.releases)) {
        return data.releases.filter(
            (release) => release?.version && releaseMatchesClinicType(release, clinicType)
        );
    }

    // Legacy single-release format from the old modal
    if (data.version && Array.isArray(data.features)) {
        const legacy = {
            version: data.version,
            date: data.date || null,
            title: data.headline || data.title || "What's New",
            description: data.description || '',
            features: data.features,
            clinicTypes: data.clinicTypes,
        };
        return releaseMatchesClinicType(legacy, clinicType) ? [legacy] : [];
    }

    return [];
}

export function hasSeenWhatsNew(version) {
    if (!version) {
        return true;
    }
    try {
        return localStorage.getItem(WHATS_NEW_STORAGE_KEY) === version;
    } catch {
        return false;
    }
}

export function markWhatsNewSeen(version) {
    if (!version) {
        return;
    }
    try {
        localStorage.setItem(WHATS_NEW_STORAGE_KEY, version);
    } catch {
        // ignore quota / private mode
    }
}

export async function fetchWhatsNew() {
    const base = process.env.PUBLIC_URL || '';
    const url = `${base}/whats-new.json?t=${Date.now()}`;
    const response = await fetch(url, {
        cache: 'no-store',
        headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
        },
    });
    if (!response.ok) {
        throw new Error(`Failed to load what's new (${response.status})`);
    }
    return response.json();
}

export function formatWhatsNewDate(value) {
    if (!value) {
        return null;
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}
